import { tKey, type I18nKey } from "@/core/i18n";
import { ProjectTypeEnum } from "@repo/schema";

export interface HyperparamsPreset {
  id: string;
  name: I18nKey<"model">;
  description: I18nKey<"model">;
  config: Record<string, unknown>;
}

// Keys are the trainer's native names. `batch` and `epochs` are intentionally
// omitted — they come from the form fields and the service's auto-sizing.
const DETECTION_PRESETS: HyperparamsPreset[] = [
  {
    id: "fast-training",
    name: tKey("model:presets.names.fastTraining"),
    description: tKey("model:presets.descriptions.fastTraining"),
    config: {
      size: "t",
      imgsz: 640,
      optimizer: "adamw",
      lr0: 0.001,
      patience: 20,
    },
  },
  {
    id: "high-accuracy",
    name: tKey("model:presets.names.highAccuracy"),
    description: tKey("model:presets.descriptions.highAccuracy"),
    config: {
      size: "c",
      imgsz: 1280,
      optimizer: "adamw",
      lr0: 0.001,
      min_lr_ratio: 0.01,
      momentum: 0.937,
      weight_decay: 0.0005,
      warmup_epochs: 5,
      patience: 50,
      mosaic_prob: 1.0,
      no_aug_epochs: 15,
      mixup_prob: 0.15,
      copy_paste: 0.1,
      translate: 0.1,
      flip_prob: 0.5,
      amp: true,
    },
  },
  {
    id: "low-memory",
    name: tKey("model:presets.names.lowMemory"),
    description: tKey("model:presets.descriptions.lowMemory"),
    config: {
      size: "t",
      imgsz: 640,
      workers: 4,
      optimizer: "adamw",
      lr0: 0.001,
      patience: 30,
    },
  },
];

// Segmentation trains at the size's own fixed resolution, so no `imgsz`, and
// its optimizer is fixed, so the learning rate is the lever.
const SEGMENTATION_PRESETS: HyperparamsPreset[] = [
  {
    id: "fast-training",
    name: tKey("model:presets.names.fastTraining"),
    description: tKey("model:presets.descriptions.fastTraining"),
    config: {
      size: "n",
      lr0: 0.0001,
      patience: 20,
    },
  },
  {
    id: "high-accuracy",
    name: tKey("model:presets.names.highAccuracy"),
    description: tKey("model:presets.descriptions.highAccuracy"),
    config: {
      size: "l",
      lr0: 0.0001,
      backbone_lr_mult: 0.1,
      weight_decay: 0.0001,
      ema_decay: 0.993,
      patience: 30,
      flip_prob: 0.5,
      crop_resize_prob: 0.5,
      amp: true,
    },
  },
  {
    id: "low-memory",
    name: tKey("model:presets.names.lowMemory"),
    description: tKey("model:presets.descriptions.lowMemory"),
    config: {
      size: "n",
      workers: 2,
      lr0: 0.0001,
      patience: 30,
    },
  },
];

export const getHyperparamsPresets = (
  trainingType: ProjectTypeEnum,
): HyperparamsPreset[] =>
  trainingType === ProjectTypeEnum.SEGMENTATION
    ? SEGMENTATION_PRESETS
    : DETECTION_PRESETS;
